import type { ChatTransport, UIMessageChunk } from "ai";
import { type Beat, findScenario, type MockScenario } from "./mock-script";
import type { OnboardingUIMessage } from "./onboarding-chat";

const CHUNK_DELAY_MS = 28;
const CHUNK_MIN_CHARS = 2;
const CHUNK_MAX_CHARS = 7;

const createRandom = (seed: number) => {
  let state = seed;
  return () => {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
};

const delay = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason);
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(signal?.reason);
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });

export class MockChatTransport implements ChatTransport<OnboardingUIMessage> {
  private readonly scenario: MockScenario;
  private readonly random = createRandom(20260907);
  private turnIndex = 0;
  private messageCounter = 0;
  private interrupted: Beat[] | undefined;
  private lastSelection: string[] = [];

  constructor(scenario?: string | MockScenario | null) {
    this.scenario =
      typeof scenario === "string"
        ? findScenario(scenario)
        : (scenario ?? findScenario(null));
  }

  get scenarioId(): string {
    return this.scenario.id;
  }

  get watchPlan(): MockScenario["watch"] {
    return this.scenario.watch;
  }

  sendMessages({
    messages,
    abortSignal,
  }: Parameters<ChatTransport<OnboardingUIMessage>["sendMessages"]>[0]) {
    this.rememberSelection(messages);
    // Opening a new message here makes the SDK carry the previous one's parts
    // into it, and the turn renders twice.
    const continuation = messages.at(-1)?.role === "assistant";
    return Promise.resolve(
      this.play(this.nextTurn(messages), abortSignal, continuation),
    );
  }

  reconnectToStream() {
    if (!this.interrupted) return Promise.resolve(null);
    const remainder = this.interrupted;
    this.interrupted = undefined;
    return Promise.resolve(this.play(remainder));
  }

  private rememberSelection(messages: OnboardingUIMessage[]): void {
    const text = messages.at(-1)?.parts.find((part) => part.type === "text");
    if (text?.type === "text" && text.text) {
      this.lastSelection = text.text.split(", ");
    }
  }

  private nextTurn(messages: OnboardingUIMessage[]): Beat[] {
    if (this.wasDenied(messages) && this.scenario.declineTurn) {
      return this.scenario.declineTurn;
    }
    const script = [this.scenario.opening, ...this.scenario.turns];
    const turn = script[this.turnIndex];
    if (!turn) return SCRIPT_EXHAUSTED;
    this.turnIndex += 1;
    return turn;
  }

  private wasDenied(messages: OnboardingUIMessage[]): boolean {
    const parts = messages.at(-1)?.parts ?? [];
    return parts.some(
      (part) =>
        "approval" in part &&
        part.approval != null &&
        typeof part.approval === "object" &&
        "approved" in part.approval &&
        part.approval.approved === false,
    );
  }

  private play(
    beats: Beat[],
    abortSignal?: AbortSignal,
    continuation = false,
  ): ReadableStream<UIMessageChunk> {
    const emit = this.emitBeats.bind(this);
    return new ReadableStream<UIMessageChunk>({
      async start(controller) {
        try {
          for await (const chunk of emit(beats, abortSignal, continuation)) {
            controller.enqueue(chunk);
          }
          controller.close();
        } catch (error) {
          controller.error(error);
        }
      },
    });
  }

  private async *emitBeats(
    beats: Beat[],
    abortSignal?: AbortSignal,
    continuation = false,
  ): AsyncGenerator<UIMessageChunk> {
    if (!continuation) {
      this.messageCounter += 1;
      yield { type: "start", messageId: `mock-${this.messageCounter}` };
    }
    yield { type: "start-step" };

    const remaining = [...beats];
    while (remaining.length > 0) {
      const beat = remaining.shift() as Beat;

      if (beat.kind === "pause") {
        await delay(beat.ms, abortSignal);
        continue;
      }

      if (beat.kind === "say") {
        if (beat.warm) yield { type: "data-tone", data: { tone: "warm" } };
        yield* this.emitText(beat.text, abortSignal);
        continue;
      }

      if (beat.kind === "tool") {
        yield {
          type: "tool-input-available",
          toolCallId: beat.id,
          toolName: beat.name,
          input: beat.input,
        };
        await delay(beat.takes ?? 800, abortSignal);
        yield {
          type: "tool-output-available",
          toolCallId: beat.id,
          output:
            beat.echoSelection && this.lastSelection.length > 0
              ? { examTypes: this.lastSelection }
              : beat.output,
        };
        continue;
      }

      if (beat.kind === "tool-approval") {
        yield {
          type: "tool-input-available",
          toolCallId: beat.id,
          toolName: beat.name,
          input: beat.input,
        };
        yield {
          type: "tool-approval-request",
          toolCallId: beat.id,
          approvalId: `${beat.id}-approval`,
          reason: beat.reason,
          approvalDescriptor: beat.descriptor,
        };
        continue;
      }

      if (beat.kind === "tool-output") {
        await delay(beat.takes ?? 800, abortSignal);
        yield {
          type: "tool-output-available",
          toolCallId: beat.id,
          output: beat.output,
        };
        continue;
      }

      if (beat.kind === "tool-failure") {
        yield {
          type: "tool-input-available",
          toolCallId: beat.id,
          toolName: beat.name,
          input: beat.input,
        };
        await delay(beat.takes ?? 800, abortSignal);
        yield {
          type: "tool-output-error",
          toolCallId: beat.id,
          errorText: beat.errorText,
        };
        continue;
      }

      if (beat.kind === "drop") {
        this.interrupted = remaining;
        throw new Error("The connection dropped");
      }

      if (beat.kind === "watch") {
        yield { type: "data-watch", id: beat.id, data: beat.data };
        continue;
      }

      if (beat.kind === "data") {
        yield {
          type: `data-${beat.part}`,
          id: beat.id,
          data: beat.data,
        } as UIMessageChunk;
        continue;
      }

      yield { type: "error", errorText: beat.text };
    }

    yield { type: "finish-step" };
    yield { type: "finish" };
  }

  private async *emitText(
    text: string,
    abortSignal?: AbortSignal,
  ): AsyncGenerator<UIMessageChunk> {
    const id = `text-${this.messageCounter}-${Math.floor(this.random() * 1e6)}`;
    yield { type: "text-start", id };
    let cursor = 0;
    while (cursor < text.length) {
      const span =
        CHUNK_MIN_CHARS +
        Math.floor(this.random() * (CHUNK_MAX_CHARS - CHUNK_MIN_CHARS));
      await delay(CHUNK_DELAY_MS, abortSignal);
      yield {
        type: "text-delta",
        id,
        delta: text.slice(cursor, cursor + span),
      };
      cursor += span;
    }
    yield { type: "text-end", id };
  }
}

const SCRIPT_EXHAUSTED: Beat[] = [
  { kind: "pause", ms: 400 },
  {
    kind: "say",
    text: "That's the end of this mock script. Pick another scenario from the picker to exercise a different state, or reload to start again.",
  },
];
